// Threaded comments for an outing, Reddit-style: replies nest and indent up
// to MAX_INDENT_DEPTH levels, and anything deeper collapses to a "Continue
// this thread" link that re-roots the view at that comment.
//
// Re-rooting stays on the outing page — it swaps the thread's root id in
// component state rather than navigating. The query is the same document
// either way (`$parentId` null for the outing's own thread, a comment id for
// a continuation), so a continuation gets four fresh levels of indent.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { CornerDownRight, MessageSquare, Trash2 } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  COMMENT_CONTEXT,
  COMMENT_SUBTREE,
  CREATE_COMMENT,
  DELETE_COMMENT,
  OUTING_COMMENTS,
  type CommentContextResult,
  type CommentNode,
  type OutingCommentsResult,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import { formatRelativeTime } from "../lib/format";
import { useT } from "../lib/i18n";
import { Avatar } from "./Avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorBox, Spinner, TextArea } from "./ui";

/**
 * How many levels of reply get visible indentation before the thread is cut
 * and continued behind a link. Level 0 is a top-level comment, so this shows
 * a root plus three generations of replies.
 */
const MAX_INDENT_DEPTH = 4;

export function CommentThread({ outingId, ownerId }: { outingId: string; ownerId: string | null }) {
  // null = the outing's own thread; a comment id = a continuation re-rooted
  // at that comment.
  const [rootId, setRootId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      {rootId && <ThreadContext rootId={rootId} onExit={() => setRootId(null)} />}
      <CommentComposer outingId={outingId} parentId={rootId} />
      <CommentList outingId={outingId} rootId={rootId} ownerId={ownerId} onContinue={setRootId} />
    </div>
  );
}

/**
 * Breadcrumb shown above a re-rooted thread: the comment being continued,
 * plus a way back to the full discussion.
 */
function ThreadContext({ rootId, onExit }: { rootId: string; onExit: () => void }) {
  const { t } = useT();
  const { data } = useQuery({
    queryKey: ["comment-context", rootId],
    queryFn: () => gqlRequest<CommentContextResult>(COMMENT_CONTEXT, { id: rootId }),
  });

  const comment = data?.outingComment;

  return (
    <div className="rounded-xl border border-alpine-200 dark:border-alpine-900 bg-alpine-50 dark:bg-alpine-950 p-4">
      <button
        type="button"
        onClick={onExit}
        className="text-sm font-semibold text-alpine-700 dark:text-alpine-300 hover:underline"
      >
        {t("comments.back")}
      </button>
      {comment && (
        <p className="mt-2 text-sm text-muted-foreground">
          {t("comments.replyingUnder")}{" "}
          <span className="font-medium text-foreground">
            {comment.person?.displayName ?? t("common.someone")}
          </span>
          :{" "}
          <span className="italic">
            {comment.deletedAt ? t("comments.deleted") : truncate(comment.body, 120)}
          </span>
        </p>
      )}
    </div>
  );
}

function CommentList({
  outingId,
  rootId,
  ownerId,
  onContinue,
}: {
  outingId: string;
  rootId: string | null;
  ownerId: string | null;
  onContinue: (id: string) => void;
}) {
  const { t } = useT();
  // Two documents, not one: pdbq drops an `equalTo` whose variable is null
  // rather than turning it into `IS NULL`, so the roots query has to hardcode
  // `parentId: { isNull: true }`. See the note in queries.ts.
  const { data, isLoading, error } = useQuery({
    queryKey: ["outing-comments", outingId, rootId],
    queryFn: () =>
      rootId
        ? gqlRequest<OutingCommentsResult>(COMMENT_SUBTREE, { parentId: rootId })
        : gqlRequest<OutingCommentsResult>(OUTING_COMMENTS, { outingId }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  const nodes = data?.outingComments.nodes ?? [];
  if (!nodes.length) {
    return <EmptyState>{t("comments.empty")}</EmptyState>;
  }

  return (
    <ul className="space-y-3">
      {nodes.map((node) => (
        <Comment
          key={node.id}
          node={node}
          outingId={outingId}
          ownerId={ownerId}
          level={0}
          onContinue={onContinue}
        />
      ))}
    </ul>
  );
}

function Comment({
  node,
  outingId,
  ownerId,
  level,
  onContinue,
}: {
  node: CommentNode;
  outingId: string;
  ownerId: string | null;
  level: number;
  onContinue: (id: string) => void;
}) {
  const { personId } = useAuth();
  const { t } = useT();
  const queryClient = useQueryClient();
  const [replying, setReplying] = useState(false);

  const deleted = node.deletedAt != null;
  // The outing's owner moderates their own outing's thread; the author
  // removes their own comment. Matches the RLS policy on outing_comment.
  const canDelete = !deleted && personId != null && (personId === node.personId || personId === ownerId);

  const deleteMutation = useMutation({
    mutationFn: () => gqlRequest(DELETE_COMMENT, { id: node.id, deletedAt: new Date().toISOString() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outing-comments", outingId] });
      queryClient.invalidateQueries({ queryKey: ["outing", outingId] });
    },
  });

  const replies = node.replies?.nodes ?? [];
  // At the indent limit the thread is cut: the link re-roots at this comment
  // so its replies get a fresh set of levels. replyCount comes from the
  // connection's totalCount, so this is accurate without fetching a level
  // deeper than the document goes.
  const atLimit = level >= MAX_INDENT_DEPTH - 1;
  const hasMore = node.replyCount.totalCount > 0;

  return (
    <li>
      <article className="rounded-xl border bg-card p-3 shadow-card">
        <header className="flex flex-wrap items-center gap-x-2 text-sm">
          {deleted ? (
            <span className="font-medium text-muted-foreground">{t("comments.deleted")}</span>
          ) : (
            <Link
              href={`/person/${node.personId}`}
              className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline"
            >
              <Avatar
                person={{ displayName: node.person?.displayName ?? null, avatarKey: node.person?.avatarKey }}
                size="sm"
              />
              {node.person?.displayName ?? t("common.someone")}
            </Link>
          )}
          {node.personId === ownerId && !deleted && <Badge variant="secondary">{t("comments.author")}</Badge>}
          <time className="text-xs text-muted-foreground" dateTime={node.createdAt}>
            {formatRelativeTime(node.createdAt)}
          </time>
        </header>

        <p className={`mt-1.5 whitespace-pre-line text-sm ${deleted ? "italic text-muted-foreground" : "text-foreground"}`}>
          {deleted ? t("comments.wasDeleted") : node.body}
        </p>

        {!deleted && (
          <div className="mt-2 flex items-center gap-3">
            {personId && (
              <button
                type="button"
                onClick={() => setReplying((r) => !r)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-primary"
              >
                <CornerDownRight aria-hidden className="size-3.5" />
                {replying ? t("common.cancel") : t("comments.reply")}
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t("comments.confirmDelete"))) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50"
              >
                <Trash2 aria-hidden className="size-3.5" />
                {deleteMutation.isPending ? t("common.deleting") : t("common.delete")}
              </button>
            )}
          </div>
        )}

        {deleteMutation.error != null && <ErrorBox error={deleteMutation.error} />}

        {replying && (
          <div className="mt-3">
            <CommentComposer
              outingId={outingId}
              parentId={node.id}
              autoFocus
              compact
              onDone={() => setReplying(false)}
            />
          </div>
        )}
      </article>

      {/* Indented children, or the continuation link once the limit is hit. */}
      {atLimit ? (
        hasMore && (
          <div className="mt-2 ml-3 border-l-2 border-alpine-200 pl-3 sm:ml-4 sm:pl-4 dark:border-alpine-900">
            <button
              type="button"
              onClick={() => onContinue(node.id)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              <MessageSquare aria-hidden className="size-3.5" />
              {t(node.replyCount.totalCount === 1 ? "comments.continueOne" : "comments.continueOther", {
                count: node.replyCount.totalCount,
              })}
            </button>
          </div>
        )
      ) : (
        replies.length > 0 && (
          <ul className="mt-2 ml-3 space-y-3 border-l-2 border-border pl-3 sm:ml-4 sm:pl-4">
            {replies.map((child) => (
              <Comment
                key={child.id}
                node={child}
                outingId={outingId}
                ownerId={ownerId}
                level={level + 1}
                onContinue={onContinue}
              />
            ))}
          </ul>
        )
      )}
    </li>
  );
}

function CommentComposer({
  outingId,
  parentId,
  autoFocus,
  compact,
  onDone,
}: {
  outingId: string;
  parentId: string | null;
  autoFocus?: boolean;
  compact?: boolean;
  onDone?: () => void;
}) {
  const { personId } = useAuth();
  const { t } = useT();
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");

  const createMutation = useMutation({
    mutationFn: () =>
      gqlRequest(CREATE_COMMENT, { outingId, personId, parentId, body: body.trim() }),
    onSuccess: () => {
      setBody("");
      queryClient.invalidateQueries({ queryKey: ["outing-comments", outingId] });
      queryClient.invalidateQueries({ queryKey: ["outing", outingId] });
      onDone?.();
    },
  });

  // Anonymous visitors read the thread but get a pointer to log in rather
  // than a dead textarea. The outing page already wraps nothing in a
  // LoginGate — browsing stays open.
  if (!personId) {
    if (compact) return null;
    return (
      <p className="rounded-xl border border-dashed bg-card/60 p-4 text-center text-sm text-muted-foreground">
        <Link href={`/login?next=${encodeURIComponent(`/outing/${outingId}`)}`} className="font-semibold text-primary hover:underline">
          {t("common.logIn")}
        </Link>{" "}
        {t("comments.loginToJoin")}
      </p>
    );
  }

  const empty = body.trim().length === 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!empty && !createMutation.isPending) createMutation.mutate();
      }}
      className="space-y-2"
    >
      <TextArea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={compact ? 2 : 3}
        autoFocus={autoFocus}
        placeholder={parentId ? t("comments.replyPlaceholder") : t("comments.addPlaceholder")}
      />
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={empty || createMutation.isPending}>
          {createMutation.isPending ? t("comments.posting") : parentId ? t("comments.reply") : t("comments.submit")}
        </Button>
        {onDone && (
          <button
            type="button"
            onClick={onDone}
            className="text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            {t("common.cancel")}
          </button>
        )}
      </div>
      {createMutation.error != null && <ErrorBox error={createMutation.error} />}
    </form>
  );
}

function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max).trimEnd()}…`;
}
